import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2, CheckCircle, Trophy, DollarSign, Coins } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/hooks/useWallet";
import { usePresaleManager } from "@/hooks/usePresaleManager";
import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { getContractAddresses, PRESALE_MANAGER_ABI, parseUSDC } from "@/lib/contracts";

interface ClaimWidgetProps {
  market: {
    id: string;
    question: string;
    creator: string;
    resolved: boolean;
    forceResolvedAnswer?: number; // 1 = YES won, 0 = NO won
  };
}

const ClaimWidget = ({ market }: ClaimWidgetProps) => {
  const { address, isConnected, connect, chain } = useWallet();
  const { claimFees } = usePresaleManager();
  const contracts = getContractAddresses(chain?.id);
  
  const [claimingType, setClaimingType] = useState<'fees' | 'lp' | 'winnings' | null>(null);
  
  const { writeContract, data: hash, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  // Check if user is the creator
  const isCreator = address?.toLowerCase() === market.creator?.toLowerCase();
  
  // Get claimable fees (for creator)
  const { data: claimableFees } = useReadContract({
    address: contracts.PRESALE_MANAGER as `0x${string}`,
    abi: PRESALE_MANAGER_ABI,
    functionName: 'getClaimableFees',
    args: [market.id as `0x${string}`, address as `0x${string}`],
    query: {
      enabled: isConnected && isCreator && !!market.id && !!address,
    }
  });

  // Get LP shares (for liquidity providers)
  const { data: lpShares } = useReadContract({
    address: contracts.PRESALE_MANAGER as `0x${string}`,
    abi: PRESALE_MANAGER_ABI,
    functionName: 'contributorLPShares',
    args: [market.id as `0x${string}`, address as `0x${string}`],
    query: {
      enabled: isConnected && !!address && !!market.id,
    }
  });

  // Determine winning token and user balance
  const winningOutcome = market.forceResolvedAnswer === 1 ? 'YES' : market.forceResolvedAnswer === 0 ? 'NO' : null;
  
  // TODO: Get user's winning token balance
  const winningTokenBalance = 0; // Will be implemented with token balance check

  // Handle claim fees (creator only)
  const handleClaimFees = async () => {
    if (!isCreator || !market.id) return;
    
    try {
      setClaimingType('fees');
      await claimFees(market.id);
      toast.success('Creator fees claimed successfully!');
    } catch (error) {
      console.error('Error claiming fees:', error);
      toast.error('Failed to claim fees');
    } finally {
      setClaimingType(null);
    }
  };

  // Handle redeem LP shares
  const handleRedeemLPShares = async () => {
    try {
      setClaimingType('lp');
      
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'redeemPresale',
        args: [market.id as `0x${string}`],
      } as any);
    } catch (error) {
      console.error('Error redeeming LP shares:', error);
      toast.error('Failed to redeem LP shares');
      setClaimingType(null);
    }
  };

  // Handle claim winnings (token holders) - using redeemPresale for now
  const handleClaimWinnings = async () => {
    try {
      setClaimingType('winnings');
      
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'redeemPresale',
        args: [market.id as `0x${string}`],
      } as any);
    } catch (error) {
      console.error('Error claiming winnings:', error);
      toast.error('Failed to claim winnings');
      setClaimingType(null);
    }
  };

  // Show success toast when transaction confirms
  useEffect(() => {
    if (isConfirmed && claimingType) {
      if (claimingType === 'lp') {
        toast.success('LP shares redeemed successfully!');
      } else if (claimingType === 'winnings') {
        toast.success('Winnings claimed successfully!');
      }
      setClaimingType(null);
    }
  }, [isConfirmed, claimingType]);

  const feesAmount = claimableFees ? parseUSDC(claimableFees as bigint) : 0;
  const lpSharesAmount = lpShares ? parseUSDC(lpShares as bigint) : 0;
  const isProcessing = isPending || isConfirming;

  if (!isConnected) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Trophy className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Claim Rewards</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Connect wallet to claim your rewards
            </p>
          </div>
          <Button onClick={connect} className="w-full">
            Connect Wallet
          </Button>
        </div>
      </Card>
    );
  }

  if (!market.resolved) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Trophy className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Market Not Resolved</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This market has not been resolved yet
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const hasAnyClaims = (isCreator && feesAmount > 0) || lpSharesAmount > 0 || winningTokenBalance > 0;

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-success" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">Market Resolved</h3>
            {winningOutcome && (
              <p className="text-sm text-muted-foreground">
                Winning outcome: <span className="font-semibold text-foreground">{winningOutcome}</span>
              </p>
            )}
          </div>
        </div>

        <Separator />

        {!hasAnyClaims ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">
              No rewards to claim for this market
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Creator Fees */}
            {isCreator && feesAmount > 0 && (
              <div className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Creator Fees</span>
                  </div>
                  <span className="font-semibold">${feesAmount.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={handleClaimFees}
                  disabled={isProcessing || claimingType !== null}
                  className="w-full"
                  size="sm"
                >
                  {claimingType === 'fees' && isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    'Claim Fees'
                  )}
                </Button>
              </div>
            )}

            {/* LP Shares */}
            {lpSharesAmount > 0 && (
              <div className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">LP Rewards</span>
                  </div>
                  <span className="font-semibold">${lpSharesAmount.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={handleRedeemLPShares}
                  disabled={isProcessing || claimingType !== null}
                  className="w-full"
                  size="sm"
                >
                  {claimingType === 'lp' && isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redeeming...
                    </>
                  ) : (
                    'Redeem LP Shares'
                  )}
                </Button>
              </div>
            )}

            {/* Winning Tokens */}
            {winningTokenBalance > 0 && (
              <div className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-success" />
                    <span className="text-sm font-medium">{winningOutcome} Token Winnings</span>
                  </div>
                  <span className="font-semibold">${winningTokenBalance.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={handleClaimWinnings}
                  disabled={isProcessing || claimingType !== null}
                  className="w-full bg-success hover:bg-success/90"
                  size="sm"
                >
                  {claimingType === 'winnings' && isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    'Claim Winnings'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            {isCreator && "As the creator, you can claim 0.5% trading fees. "}
            {lpSharesAmount > 0 && "You provided liquidity during the launchpad phase. "}
            {winningTokenBalance > 0 && `Your ${winningOutcome} tokens can be redeemed for USDT.`}
          </p>
        </div>
      </div>
    </Card>
  );
};

export default ClaimWidget;

